import React, { ChangeEvent, SyntheticEvent, useEffect, useState, useCallback } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormGroup from "@mui/material/FormGroup";
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from "@mui/material/RadioGroup";
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuIcon from '@mui/icons-material/Menu';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from "@mui/material/Tooltip";
import { COMPARISON_CASH_COMES_FROM_LIQUIDATION, COMPARISON_CASH_COMES_FROM_CASH_FLOW, ComparisonCashComesFrom, createPortfolio, Portfolio } from "./create-portfolio";
import './App.css';
import Container from "@mui/material/Container";
import { AppBar, MenuItem, IconButton, SlideProps, Toolbar, useScrollTrigger, useTheme, Menu, Chip } from "@mui/material";
import Slide from '@mui/material/Slide';
import Spacer from "./Spacer";
import PortfolioTable from "./PortfolioTable";
import Grid from "@mui/material/Grid";
import Footer from './Footer';
import { useContactEmail } from "./hooks/useContactEmail";
import IncomeConfig from "./forms/IncomeConfig";

import { ConveyorBelt, LoadingSpinner } from './svg';
import { ThemeContextProvider } from './context/ThemeContext';
import ThemeSwitcher from './components/ThemeSwitcher';
import CollapsibleBox from './components/CollapsibleBox';

const COC = 7;
const EQUITY_MULTIPLE = 1.8;
const DEFAULT_INVEST_PER_YEAR = 100 * 1000;
const DEFAULT_STOP_INVESTING_AT = 6;

const DEFAULT_COMPARISON_RETURN_PCT = 7.5;
const DEFAULT_COMPARISON_LIQUIDATION_RATE_PCT = 4;

const DEFAULT_COMPARISON_CASH_COMES_FROM = COMPARISON_CASH_COMES_FROM_LIQUIDATION;

const DEFAULT_INCOME = 300000;
const DEFAULT_EXPECTED_YEARLY_INCOME_INCREASE = .05;

const getComparisonHelpTextForOrigin = (origin: ComparisonCashComesFrom) => {
    if (origin === COMPARISON_CASH_COMES_FROM_CASH_FLOW) {
        return 'This is the yearly rate at which your investment cash flows.';
    }

    return 'This is the rate at which you\'ll liquidate your portfolio per year for passive income.'
};

const HighlightGreen: React.FC<React.PropsWithChildren> = ({ children }) => {
    const theme = useTheme();
    return (
        <div style={{ padding: theme.spacing(2), backgroundColor: theme.palette.secondary.light, color: theme.palette.secondary.contrastText }}>
            {children}
        </div>
    );
};

interface HideOnScrollProps extends SlideProps {
    children: React.ReactElement;
}

const HideOnScroll = ({ children, ...rest }: HideOnScrollProps) => {
    const trigger = useScrollTrigger();

    return (
        <Slide appear={false} direction="down" in={!trigger} {...rest}>
            {children}
        </Slide>
    );
}

interface EnterYearControlProps {
    value: number;
    onChange: (value: number) => void;
    label: string;
    tooltip?: string;
}

const EnterYearControl: React.FC<EnterYearControlProps> = ({ value, onChange, label, tooltip }) => {
    const [lastValueStr, setLastValueStr] = useState(`${value === Infinity ? '' : value}`);
    const [never, setNever] = useState<boolean>(value === Infinity);

    const handleYearChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValueStr = event.target.value;
        setLastValueStr(newValueStr);
    };

    const handleNeverChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setNever(newValue);
    };

    useEffect(() => {
        let finalValue = 0;
        const textYearsVal = parseInt(lastValueStr);

        if (never || isNaN(textYearsVal)) {
            finalValue = Infinity;
        } else {
            finalValue = textYearsVal;
        }

        onChange(finalValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [never, lastValueStr])

    const getYearValue = () => {
        if (value === Infinity) {
            return lastValueStr;
        }
        return `${value}`;
    };

    return (
        <>
            <Tooltip title={tooltip}>
                <TextField label={label} onChange={handleYearChange} value={getYearValue()} disabled={never} />
            </Tooltip>
            <FormControlLabel
                control={<Checkbox onChange={handleNeverChange} checked={never} />}
                label="Never"
            />
        </>
    )
}

export const FormFieldError: React.FC<{ message?: string }> = ({ message = '' }) => {
    const theme = useTheme();

    return (
        <div style={{
            minHeight: theme.spacing(3),
            color: theme.palette.error.main,
            fontSize: '12px',
            textAlign: 'left',
            padding: theme.spacing(1),
        }}>
            {message}
        </div>
    );
}

export interface FormErrors {
    amountInvestedPerYear?: string;
    cocReturn?: string;
    equityMultiple?: string;
    stopInvestingYear?: string;
    stopReinvestingDistributionsYear?: string;
    comparisonReturnPct?: string;
    comparisonLiquidationRatePct?: string;
}

interface LoginFormProps {
    onSubmit: (email: string) => void;
    loading: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, loading }) => {
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<string>();

    // https://stackoverflow.com/questions/39356826/how-to-check-if-it-a-text-input-has-a-valid-email-format-in-reactjs
    // eslint-disable-next-line no-useless-escape
    let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const handleSubmit = (e: SyntheticEvent) => {
        e.preventDefault();

        if (email && re.test(email)) {
            // valid
            setError(undefined);
            onSubmit(email);
        } else {
            setError('Please provide a valid email.');
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <FormGroup className="form-group">
                <TextField
                    label="Email"
                    onChange={(e) => { setEmail(e.target.value) }}
                    value={email}
                    error={!!error}
                />
                <FormFieldError message={error} />
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <Button type="submit">Submit</Button>
                )}
            </FormGroup>
        </form>
    );
};

function App() {
    const theme = useTheme();
    const [contactEmail, setContactEmail, contactEmailLoading] = useContactEmail();

    const [amountInvestedPerYear, setAmountInvestedPerYear] = useState<string>(`${DEFAULT_INVEST_PER_YEAR}`);
    const [cocReturnPct, setCocReturnPct] = useState<string>(`${COC}`);
    const [equityMultiple, setEquityMultiple] = useState<string>(`${EQUITY_MULTIPLE}`);
    const [stopInvestingYear, setStopInvestingYear] = useState(DEFAULT_STOP_INVESTING_AT);
    const [reinvestDistributions, setReinvestDistributions] = useState<boolean>(false);
    const [stopReinvestingDistributionsYear, setStopReinvestingDistributionsYear] = useState(0)
    const [portfolio, setPortfolio] = useState<Portfolio>();
    const [page, setPage] = useState(0);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [name, setName] = useState<string>();

    const [comparisonReturnPct, setComparisonReturnPct] = useState<string>(`${DEFAULT_COMPARISON_RETURN_PCT}`)
    const [comparisonLiquidationRatePct, setComparisonLiquidationRatePct] = useState<string>(`${DEFAULT_COMPARISON_LIQUIDATION_RATE_PCT}`)
    const [comparisonCashComesFrom, setComparisonCashComesFrom] = useState<ComparisonCashComesFrom>(DEFAULT_COMPARISON_CASH_COMES_FROM);
    const [income, setIncome] = useState<number>(DEFAULT_INCOME);
    const [expectedIncomeIncrease, setExpectedIncomeIncrease] = useState<number>(DEFAULT_EXPECTED_YEARLY_INCOME_INCREASE);
    const [includeTaxes, setIncludeTaxes] = useState<boolean>(false);

    const handleSubmit = () => {
        const valid = validateForm();

        if (!valid) {
            return;
        }

        const amountVal = amountInvestedPerYear ? parseInt(amountInvestedPerYear) : 0;
        const cocReturnPctVal = cocReturnPct ? parseInt(cocReturnPct) : 0;
        const equityMultipleVal = equityMultiple ? parseFloat(equityMultiple) : 0;

        const comparison = {
            returnRate: comparisonReturnPct ? parseInt(comparisonReturnPct) / 100 : 0,
            liquidationRate: comparisonLiquidationRatePct ? parseInt(comparisonLiquidationRatePct) / 100 : 0,
            cashDistributionsComeFrom: comparisonCashComesFrom,
        };

        const finalStopReinvestingDistributions = reinvestDistributions ? stopReinvestingDistributionsYear : 0;


        const portfolio = createPortfolio(amountVal, cocReturnPctVal / 100, equityMultipleVal, stopInvestingYear, finalStopReinvestingDistributions, comparison, {
            baseIncome: income,
            increasePerYear: expectedIncomeIncrease
        }, undefined, includeTaxes);

        setPortfolio(portfolio);
        setPage(1);
    };

    const validateForm = useCallback((): boolean => {
        const MINIMUM_YEARS_INVESTING = 6;

        const newErrors: FormErrors = {};
        let valid = true;

        const validateNumberMetric = ({
            metric,
            metricName,
            metricPropName,
            positiveOnly = false,
        }: {
            metric: string,
            metricName: string,
            metricPropName: keyof FormErrors,
            positiveOnly?: boolean
        }) => {
            const val = parseInt(metric);
            if (isNaN(val)) {
                newErrors[metricPropName] = `${metricName} must be a number.`;
                valid = false;
            } else if (positiveOnly && val <= 0) {
                newErrors[metricPropName] = `${metricName} must be greater than 0.`;
                valid = false;
            }
        }

        validateNumberMetric({ metric: amountInvestedPerYear, metricName: 'Amount invested per year', metricPropName: 'amountInvestedPerYear', positiveOnly: true });
        validateNumberMetric({ metric: cocReturnPct, metricName: 'Cash-on-cash return', metricPropName: 'cocReturn', positiveOnly: true });
        validateNumberMetric({ metric: equityMultiple, metricName: 'Equity multiple', metricPropName: 'equityMultiple', positiveOnly: true });
        validateNumberMetric({ metric: comparisonReturnPct, metricName: 'Comparison return', metricPropName: 'comparisonReturnPct'});
        validateNumberMetric({ metric: comparisonLiquidationRatePct, metricName: 'Comparison liquidation rate', metricPropName: 'comparisonLiquidationRatePct'});

        if (stopInvestingYear < MINIMUM_YEARS_INVESTING) {
            newErrors.stopInvestingYear = `You can't stop investing before year 6.`;
            valid = false;
        }

        if (reinvestDistributions && !stopReinvestingDistributionsYear) {
            newErrors.stopReinvestingDistributionsYear = 'Please specify a year to stop reinvesting distributions.';
            valid = false
        }

        setFormErrors(newErrors);

        return valid;
    }, [
        amountInvestedPerYear,
        cocReturnPct,
        equityMultiple,
        stopInvestingYear,
        reinvestDistributions,
        stopReinvestingDistributionsYear,
        comparisonReturnPct,
        comparisonLiquidationRatePct,
    ]);

    useEffect(() => {
        validateForm();
    }, [
        validateForm,
    ])

    const renderDescriptionSection = (showGraphic = true) => (
        <div>
            <Typography variant="h1" color={theme.palette.primary.main}>
                Conveyor Belt Wealth Simulator
            </Typography>
            <Typography variant="body1">
                See how real estate syndications can <b>accelerate your wealth</b> with this free simulator!
            </Typography>
            <Spacer size="three" vertical />
            <Typography variant="body2">
                <b>Conveyor belt wealth</b> is a framework that helps an investor supercharge their return through consistently investing in historically stable, cash-flowing real estate syndications.
            </Typography>
            <Spacer size="three" vertical />
            <HighlightGreen>
                <Typography variant="body1" style={{ color: theme.palette.secondary.contrastText }}>
                    <strong>
                        How much can you make in 20 years on the Conveyor Belt?
                    </strong>
                </Typography>
            </HighlightGreen>
            <Box
                sx={{
                    marginTop: theme.spacing(4),
                    textAlign: 'center',
                    overflow: 'hidden',
                }}
            >
                {showGraphic && (
                    <ConveyorBelt />
                )}
            </Box>
        </div>
    )
    const renderLogin = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                flexDirection: 'row',
            }}
        >
            <Container style={{
                maxWidth: 700,
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 10,
                backgroundColor: theme.palette.background.default,
                borderRadius: theme.spacing(1),
                padding: theme.spacing(3),
            }}>
                <Box p={2} width="100%" display="flex" justifyContent="flex-end">
                    <ThemeSwitcher />
                </Box>
                {renderDescriptionSection(false)}
                <Spacer size="four" vertical />
                <Typography variant="body1">
                    To get started, enter your email.
                </Typography>
                <Spacer size="four" vertical />
                <Grid item xs={12} sm={6}>
                    <LoginForm onSubmit={setContactEmail} loading={contactEmailLoading} />
                </Grid>
            </Container>
        </Box>
    )

    const renderForm = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    flexDirection: 'column',
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {/* Left Panel - Image/Content */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    height: '100%',
                                    padding: theme.spacing(4),
                                    backgroundColor: theme.palette.grey[100],
                                    borderRadius: theme.spacing(2),
                                }}
                            >
                                {renderDescriptionSection()}
                            </Box>
                        </Grid>

                        {/* Right Panel - Form */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: theme.palette.background.default,
                                    borderRadius: theme.spacing(2),
                                    padding: theme.spacing(4),
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup>
                                            <TextField
                                                label="Your name"
                                                onChange={(e) => { setName(e.target.value) }} value={name}
                                                helperText="We don't save this information."
                                            />
                                        </FormGroup>
                                        <Spacer size="two" vertical />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <TextField label="Amount to invest/year" onChange={(e) => { setAmountInvestedPerYear(e.target.value) }} value={amountInvestedPerYear} />
                                            <FormFieldError message={formErrors.amountInvestedPerYear} />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                                <CollapsibleBox title="Change investment parameters">
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <EnterYearControl
                                                value={stopInvestingYear}
                                                onChange={setStopInvestingYear}
                                                label="Stop investing in year"
                                                tooltip="This is the year you'll stop putting money in...but the conveyor belt keeps rolling."
                                            />
                                            <FormFieldError message={formErrors.stopInvestingYear} />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <Tooltip title="Cash-on-cash return is a metric of how much cash flow you receive per year in an investment. It's a measure of cash flow per year divided by the amount of money you put in.">
                                                <TextField label="Cash-on-cash return" onChange={(e) => { setCocReturnPct(e.target.value) }} value={cocReturnPct} helperText="Enter a percentage, like 7 for 7%" />
                                            </Tooltip>
                                            <FormFieldError message={formErrors.cocReturn} />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <Tooltip title="Equity multiple is your total return divided by your initial investment. An investment of $100 that yields $200 total has an equity multiple of 2.">
                                                <TextField label="Equity multiple" onChange={(e) => { setEquityMultiple(e.target.value) }} value={equityMultiple} />
                                            </Tooltip>
                                            <FormFieldError message={formErrors.equityMultiple} />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <Tooltip title="If you select this, you'll take your yearly cash flow distributions and re-invest them the next year, instead of using them as passive income.">
                                                <FormControlLabel
                                                    control={<Checkbox onChange={(e) => { setReinvestDistributions(e.target.checked) }} checked={reinvestDistributions} />}
                                                    label="Reinvest cash flow distributions?"
                                                />
                                            </Tooltip>
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {reinvestDistributions && (
                                            <FormGroup className="form-group">
                                                <EnterYearControl value={stopReinvestingDistributionsYear} onChange={setStopReinvestingDistributionsYear} label="Stop reinvesting distributions in year" />
                                                <FormFieldError message={formErrors.stopReinvestingDistributionsYear} />
                                            </FormGroup>
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
                                            <FormGroup className="form-group">
                                                <FormControlLabel
                                                    control={<Checkbox onChange={(e) => { setIncludeTaxes(e.target.checked) }} checked={includeTaxes} />}
                                                    label="Calculate taxes"
                                                />
                                            </FormGroup>
                                            <Chip label="BETA" color="primary" size="small" />
                                        </Box>
                                    </Grid>
                                </CollapsibleBox>
                                <Spacer size="two" vertical />
                                <CollapsibleBox title="Edit income">
                                    <Grid item xs={12}>
                                        <IncomeConfig
                                            income={{ value: income, setIncome }}
                                            expectedYearlyIncrease={{ value: expectedIncomeIncrease, setExpectedYearlyIncrease: setExpectedIncomeIncrease }}
                                            formErrors={formErrors}
                                        />
                                    </Grid>
                                </CollapsibleBox>
                                <Spacer size="two" vertical />
                                <CollapsibleBox title="Edit comparison investment">
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ textAlign: 'left', padding: 1 }}>
                                            The comparison investment will let us see how the Conveyor Belt framework with real estate syndications stacks up against other kinds of investments.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <Tooltip title="This is the yearly return you'd expect from a traditional investment like the S&P 500.">
                                                <TextField label="Comparison return" onChange={(e) => { setComparisonReturnPct(e.target.value) }} value={comparisonReturnPct} helperText="Enter a percentage, like 7.5 for 7.5%" />
                                            </Tooltip>
                                            <FormFieldError message={formErrors.comparisonReturnPct} />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormGroup className="form-group">
                                            <Tooltip title={getComparisonHelpTextForOrigin(comparisonCashComesFrom)}>
                                                <TextField label="Comparison liquidation rate" onChange={(e) => { setComparisonLiquidationRatePct(e.target.value) }} value={comparisonLiquidationRatePct} helperText="Enter a percentage, like 4 for 4%" />
                                            </Tooltip>
                                            <FormFieldError message={formErrors.comparisonLiquidationRatePct} />
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormGroup>
                                            <FormControl>
                                                <FormLabel>Where does the comparison cash come from?</FormLabel>
                                                <RadioGroup
                                                    value={comparisonCashComesFrom}
                                                    onChange={(e) => setComparisonCashComesFrom(e.target.value as ComparisonCashComesFrom)}
                                                >
                                                    <FormControlLabel value={COMPARISON_CASH_COMES_FROM_LIQUIDATION} control={<Radio />} label="Liquidation" />
                                                    <FormControlLabel value={COMPARISON_CASH_COMES_FROM_CASH_FLOW} control={<Radio />} label="Cash flow" />
                                                </RadioGroup>
                                            </FormControl>
                                        </FormGroup>
                                    </Grid>
                                </CollapsibleBox>
                                <Spacer size="four" vertical />
                                <div>
                                    <Button color="primary" variant="contained" onClick={handleSubmit}>Create my wealth plan</Button>
                                    <Spacer size="two" />
                                    <Button variant="outlined" href="https://ursa-capital.com/join" target="_blank">Learn more about investing</Button>
                                </div>
                                <Spacer size="four" vertical />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
                <Footer />
            </Box>
        );
    };

    const renderTable = () => {
        if (portfolio) {
            return <PortfolioTable portfolio={portfolio} name={name} />
        }
        return null;
    }

    const renderPage = (page: number) => {
        switch (page) {
            case 0:
                return renderForm();
            case 1:
                return renderTable();
            default:
                return <></>
        }
    };

    const renderNavigation = () => {
        if (page === 0 && contactEmail) {
            return (
                <header className="Form-app-header" style={{ backgroundColor: theme.palette.background.default }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <ThemeSwitcher />
                            <Spacer size="two" />
                            Hi, {contactEmail}
                        </Box>
                    </Container>
                </header>
            );
        }
        return (
            <HideOnScroll className="header-hide-on-scroll no-print">
                <AppBar position="fixed" className="header">
                    <ReportToolbar onBack={() => { setPage(0) }} />
                </AppBar>
            </HideOnScroll>
        );
    };

    return (
        <div className="App">
            <CssBaseline />
            <Container maxWidth="xl" disableGutters>
                {contactEmail ? (
                    <>
                        {renderNavigation()}
                        {renderPage(page)}
                    </>
                ) : (
                    <>
                        {renderLogin()}
                    </>
                )}
            </Container>
        </div>
    );
}

const ReportToolbar: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    return (
        <Toolbar>
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                    size="large"
                    aria-label="menu"
                    aria-controls="menu-report-toolbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                >
                    <MenuIcon />
                </IconButton>
                <Menu
                    id="menu-report-toolbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                >
                    <MenuItem onClick={onBack}>
                        <Typography sx={{ textAlign: 'center' }}>Back</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => { window.print() }}>
                        <Typography sx={{ textAlign: 'center' }}>Print your report</Typography>
                    </MenuItem>
                    <MenuItem href="https://ursa-capital.com/join" target="_blank">
                        <a href="https://ursa-capital.com/join" target="_blank" rel="noreferrer">
                            <Typography sx={{ textAlign: 'center' }}>Start investing now</Typography>
                        </a>
                    </MenuItem>
                </Menu>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                <Button color="inherit" variant="outlined" onClick={onBack} type="button">Back</Button>
                <Spacer size="two" />
                <Button color="inherit" variant="outlined" onClick={() => { window.print() }}>Print your report</Button>
                <Spacer size="two" />
                <Button target="_blank" color="secondary" variant="contained" href="https://ursa-capital.com/join">
                    Start investing now
                </Button>
                <Spacer size="two" />
            </Box>
            <ThemeSwitcher />
        </Toolbar>
    )
}

const AppWithTheme = () => (
    <ThemeContextProvider>
        <App />
    </ThemeContextProvider>
);

export default AppWithTheme;
